import { useSearchParams } from 'react-router-dom';
import { FolderHeader, type ContentTab } from './FolderHeader';
import { DocumentBreadcrumb } from './DocumentBreadcrumb';
import { DocumentsFolderContent } from './DocumentsFolderContent';
import { DocumentContentTab } from '../../constants';
import type {
  DocumentNodeWithDetailsDto,
  UserCompanyRole,
  BreadcrumbItemDto,
} from '@monorepo/shared';
import { hasPermission, Permission } from '@monorepo/shared';

interface DocumentsFolderViewProps {
  companyId: string;
  currentParentId: string | null;
  breadcrumb: BreadcrumbItemDto[];
  folders: DocumentNodeWithDetailsDto[];
  documents: DocumentNodeWithDetailsDto[];
  archivedDocuments: DocumentNodeWithDetailsDto[];
  oldVersionsCount?: number;
  isLoading: boolean;
  currentUserRole?: UserCompanyRole | undefined;
  onNavigate: (nodeId: string | null) => void;
  onRefresh: () => void;
  onAddFolder: () => void;
  onAddDocument: () => void;
  onOpenFolder: (nodeId: string) => void;
  onOpenDocument: (nodeId: string) => void;
  onRename: (node: DocumentNodeWithDetailsDto) => void;
  onDelete: (node: DocumentNodeWithDetailsDto) => void;
  onDiffuse?: (doc: DocumentNodeWithDetailsDto) => void;
  onResend?: (doc: DocumentNodeWithDetailsDto) => void;
  onSendForValidation?: (doc: DocumentNodeWithDetailsDto) => void;
  onValidate?: (doc: DocumentNodeWithDetailsDto) => void;
  onReject?: (doc: DocumentNodeWithDetailsDto) => void;
  onUploadNewVersion?: (doc: DocumentNodeWithDetailsDto) => void;
  onArchive?: (doc: DocumentNodeWithDetailsDto) => void;
  onShowReaders?: (doc: DocumentNodeWithDetailsDto) => void;
  loadingDocumentId?: string | null;
  highlightedNodeId?: string | null;
}

export const DocumentsFolderView = ({
  companyId,
  currentParentId,
  breadcrumb,
  folders,
  documents,
  archivedDocuments,
  oldVersionsCount = 0,
  isLoading,
  currentUserRole,
  onNavigate,
  onRefresh,
  onAddFolder,
  onAddDocument,
  onOpenFolder,
  onOpenDocument,
  onRename,
  onDelete,
  onDiffuse,
  onResend,
  onSendForValidation,
  onValidate,
  onReject,
  onUploadNewVersion,
  onArchive,
  onShowReaders,
  loadingDocumentId,
  highlightedNodeId,
}: DocumentsFolderViewProps) => {
  const showFolderTabs = breadcrumb.length > 1;

  const [searchParams, setSearchParams] = useSearchParams();

  const handleTabChange = (tab: ContentTab) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', tab);
    setSearchParams(nextParams, { replace: true });
  };

  const canCreateFolder = currentUserRole
    ? hasPermission(currentUserRole, Permission.CREATE_FOLDER)
    : false;
  const canAddDocument = currentUserRole
    ? hasPermission(currentUserRole, Permission.ADD_DOCUMENT)
    : false;
  const canRenameFolder = currentUserRole
    ? hasPermission(currentUserRole, Permission.RENAME_FOLDER)
    : false;
  const canDeleteFolder = currentUserRole
    ? hasPermission(currentUserRole, Permission.DELETE_FOLDER)
    : false;
  const canRenameDocument = currentUserRole
    ? hasPermission(currentUserRole, Permission.RENAME_DOCUMENT)
    : false;
  const canDeleteDocument = currentUserRole
    ? hasPermission(currentUserRole, Permission.DELETE_DOCUMENT)
    : false;
  const canDiffuseDocument = currentUserRole
    ? hasPermission(currentUserRole, Permission.DIFFUSE_DOCUMENT)
    : false;
  const canSendForValidation = currentUserRole
    ? hasPermission(currentUserRole, Permission.SEND_FOR_VALIDATION)
    : false;
  const canValidateDocument = currentUserRole
    ? hasPermission(currentUserRole, Permission.VALIDATE_DOCUMENT)
    : false;
  const canUploadNewVersion = currentUserRole
    ? hasPermission(currentUserRole, Permission.UPLOAD_NEW_VERSION)
    : false;
  const canViewOldVersions = currentUserRole
    ? hasPermission(currentUserRole, Permission.VIEW_OLD_DOCUMENT_VERSION)
    : false;
  const canArchiveDocument = currentUserRole
    ? hasPermission(currentUserRole, Permission.ARCHIVE_DOCUMENT)
    : false;

  const availableTabs: DocumentContentTab[] = [
    DocumentContentTab.FOLDERS,
    ...(showFolderTabs ? [DocumentContentTab.DOCUMENTS] : []),
    ...(canViewOldVersions && showFolderTabs ? [DocumentContentTab.OLD_VERSIONS] : []),
    ...(showFolderTabs ? [DocumentContentTab.ARCHIVED] : []),
  ];
  const defaultTab =
    folders.length === 0 && showFolderTabs && !isLoading
      ? DocumentContentTab.DOCUMENTS
      : DocumentContentTab.FOLDERS;
  const highlightTab = highlightedNodeId
    ? (
        [
          [DocumentContentTab.DOCUMENTS, documents],
          [DocumentContentTab.ARCHIVED, archivedDocuments],
          [DocumentContentTab.FOLDERS, folders],
        ] as const
      ).find(
        ([tab, nodes]) =>
          availableTabs.includes(tab) && nodes.some((node) => node.id === highlightedNodeId),
      )?.[0]
    : undefined;

  const requestedTab = searchParams.get('tab');
  const activeTab: ContentTab =
    requestedTab !== null && (availableTabs as string[]).includes(requestedTab)
      ? (requestedTab as ContentTab)
      : (highlightTab ?? defaultTab);

  const canAdd =
    activeTab === DocumentContentTab.FOLDERS
      ? canCreateFolder
      : activeTab === DocumentContentTab.DOCUMENTS
        ? canAddDocument
        : false;

  const handleAdd = () => {
    if (activeTab === DocumentContentTab.FOLDERS) {
      onAddFolder();
    } else {
      onAddDocument();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <DocumentBreadcrumb items={breadcrumb} onNavigate={onNavigate} />
      </div>

      <FolderHeader
        onRefresh={onRefresh}
        onAdd={handleAdd}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        folderCount={folders.length}
        documentCount={documents.length}
        oldVersionsCount={oldVersionsCount}
        archivedDocumentsCount={archivedDocuments.length}
        canAdd={canAdd}
        showDocumentsTab={showFolderTabs}
        showOldVersionsTab={canViewOldVersions && showFolderTabs}
        showArchivedTab={showFolderTabs}
      />

      <div className="flex-1 p-4">
        <DocumentsFolderContent
          activeTab={activeTab}
          companyId={companyId}
          currentParentId={currentParentId}
          folders={folders}
          documents={documents}
          archivedDocuments={archivedDocuments}
          isLoading={isLoading}
          canRenameFolder={canRenameFolder}
          canDeleteFolder={canDeleteFolder}
          canRenameDocument={canRenameDocument}
          canDeleteDocument={canDeleteDocument}
          canDiffuseDocument={canDiffuseDocument}
          canSendForValidation={canSendForValidation}
          canValidateDocument={canValidateDocument}
          canUploadNewVersion={canUploadNewVersion}
          canArchiveDocument={canArchiveDocument}
          onOpenFolder={onOpenFolder}
          onOpenDocument={onOpenDocument}
          onRename={onRename}
          onDelete={onDelete}
          onDiffuse={onDiffuse}
          onResend={onResend}
          onSendForValidation={onSendForValidation}
          onValidate={onValidate}
          onReject={onReject}
          onUploadNewVersion={onUploadNewVersion}
          onArchive={onArchive}
          onShowReaders={onShowReaders}
          loadingDocumentId={loadingDocumentId}
          highlightedNodeId={highlightedNodeId}
        />
      </div>
    </div>
  );
};
